import React, { useEffect, useState, useContext, useRef } from 'react'
import ConfigContext from '../contexts/configContext'
import Button from '@mui/material/Button';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import StopScreenShareIcon from '@mui/icons-material/StopScreenShare';
import DialpadIcon from '@mui/icons-material/Dialpad';
import SettingsIcon from '@mui/icons-material/Settings';
import TableRowsIcon from '@mui/icons-material/TableRows';
import GroupsIcon from '@mui/icons-material/Groups';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import MarkChatUnreadIcon from '@mui/icons-material/MarkChatUnread';
import useWindowSize from '../hooks/useWindowSize';
import { Icon, IconButton, Tooltip } from '@mui/material';
import {RTCVideo} from '@dmc/react-rtc-media-components';
import CallTimer from './CallTimer';

export default function 
CallControls(
    {
        cameraAllowed,
        hasDummyVideoTrack,
        hideDelay = 5000,
        muted = false,
        videomuted = false,
        fullscreen = false,
        screensharingAllowed = false,
        dtmfEnabled = true,
        screensharing = false,
        chatEnabled = true,
        confbridgeChannels,
        confbridgeModerator,
        drawerContent,
        dtmfActions,
        setDrawerContent,
        localVideoStream,
        onEnd,
        onMute,
        onVideoMute,
        onFullscreen,
        onScreenshare,
        unreadChatMessages,
        conferenceSettings,
    }) {
  const config = useContext(ConfigContext)
  const [visible, setVisible] = useState(false)
  const windowSize = useWindowSize()


  useEffect(() => {
    var timer = null;

    document.addEventListener('mousemove', (e) => {
        if (!visible) setVisible(true)
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => {
            setVisible(false)
        }, hideDelay)
    });
    
  }, [])

  const renderDTMFButton = () => {
    if (confbridgeModerator) return
    let component
    if (dtmfEnabled) {
      component = 
            <div className='flex-child'>
              {renderButton(
                <DialpadIcon fontSize="medium" /> 
                , 'Keypad'
                , (event) => {
                  if (!drawerContent || drawerContent !== 'Keypad') {
                    setDrawerContent('Keypad')
                  } else {
                    setDrawerContent(null)
                  }
                }
              )}
            </div>
    }
    return component
  }

  const renderSettingsButton = () => {
    if (confbridgeModerator) return
    let  component = 
          <div className='flex-child'>
            {renderButton(
              <SettingsIcon fontSize="medium" /> 
              , 'Settings'
              , (event) => {
                if (!drawerContent || drawerContent !== 'Settings') {
                  setDrawerContent('Settings')
                } else {
                  setDrawerContent(null)
                }
              }
            )}
          </div>
    return component
  }

  const renderParticipantsButton = () => {
    let component = null
    if (confbridgeChannels.length > 0) {
      component = 
            <div className='flex-child'>
              {renderButton(
                <GroupsIcon fontSize="medium" /> 
                , 'Participants'
                , (event) => {
                  if (!drawerContent || drawerContent !== 'Participants') {
                    setDrawerContent('Participants')
                  } else {
                    setDrawerContent(null)
                  }
                }
              )}
            </div>
    }
    return component
  }

  const renderDtmfActionsButton = () => {
    if (confbridgeModerator) return
    let  component = 
          <div className='flex-child'>
              {renderButton(
                <TableRowsIcon fontSize="medium" /> 
                , 'Actions'
                , (event) => {
                  if (!drawerContent || drawerContent !== 'Actions') {
                    setDrawerContent('Actions')
                  } else {
                    setDrawerContent(null)
                  }
                }
              )}
          </div>
    return component
  }

  const renderScreenshareButton = () => {
    if (confbridgeModerator) return
    let component = null
    if (screensharingAllowed) {
      component = 
            <div className='flex-child'>
              {screensharing ? 
                renderButton(
                  <StopScreenShareIcon color='primary' fontSize="medium" />
                  , 'Stop Sharing'
                  , () => onScreenshare(!screensharing)
                )
              : 
                renderButton(
                  <ScreenShareIcon fontSize="medium" /> 
                  , 'Share Screen'
                  , () => onScreenshare(!screensharing)
                )
              }

            </div>
    }
    return component
  }

  const renderMicButton = () => {
    if (confbridgeModerator) return
    let component =
      <div className='flex-child'>
          {muted ? 
            renderButton(
              <MicOffIcon fontSize="medium" /> 
              , 'Unmute'
              , () => onMute(!muted)
            )
          : 
            renderButton(
              <MicIcon color='primary' fontSize="medium" />
              , 'Mute'
              , () => onMute(!muted)
            )
          }
      </div>
    return component
  }

  const renderCamButton = () => {
    if (confbridgeModerator) return
    let button
    if (screensharing) {
      button = renderButton(
        <VideocamOffIcon fontSize="medium" /> 
        , 'Disabled'
        , () => {}
        , true
      )
    } else if (videomuted) {
      button = renderButton(
        <VideocamOffIcon fontSize="medium" /> 
        , 'Enable Video'
        , () => onVideoMute(!videomuted)
      )
    } else {
      button = renderButton(
        <VideocamIcon color='primary' fontSize="medium" />
        , 'Disable Video'
        , () => onVideoMute(!videomuted)
        , screensharing
      )
    }
    let component =
      <div className='flex-child'>
        {button}
      </div>
    return component
  }

  const renderFullscreenButton = () => {
    let component =
      <div className='flex-child'>
          {fullscreen ? 
            renderButton(
              <FullscreenExitIcon fontSize="medium" /> 
              , 'Exit Fullscreen'
              , () => onFullscreen(!fullscreen)

            )
          : 
            renderButton(
              <FullscreenIcon fontSize="medium" /> 
              , 'Fullscreen'
              , () => onFullscreen(!fullscreen)

            )
          }
      </div>
    return component
  }

  const renderChatButton = () => {
    let button = unreadChatMessages ? <MarkChatUnreadIcon fontSize='medium' /> : <ChatBubbleIcon fontSize='medium'/>
    let component
    if (confbridgeChannels.length > 0) {
      component =
        <div className='flex-child'>
            {renderButton(
              button
              , 'Conversation'
              , (event) => {
                if (!drawerContent || drawerContent !== 'Conversation') {
                  setDrawerContent('Conversation')
                } else {
                  setDrawerContent(null)
                }
              }

            )}
        </div>
    }
    return component
  }

  const renderButton = (icon, tooltip, onClick, disabled) => {
            return (
              <Tooltip title={tooltip} arrow>
                <IconButton
                  onClick={onClick}
                  disabled={disabled}
                >
                  {icon}
                </IconButton>
              </Tooltip>
            )

  }

  let containerStyle = windowSize.portrait ? 'flex-container-col' : 'flex-container'

  return (
    <>
    {!hasDummyVideoTrack || screensharing ?
      <div className={`live-call-call-local-video ${visible ? "show": "hide"}`}>
        <RTCVideo
          srcObject={localVideoStream}
          style={{
            maxWidth: "100%",
            height: "100%",
            borderRadius: "0.4rem",
            objectFit: "contain",
            transform: screensharing ? "rotateY(0deg)" : "rotateY(180deg)",
            MozTransform: screensharing ? "rotateY(0deg)" : "rotateY(180deg)"
          }}
          autoPlay
          muted={true}
        />
      </div>
    : null
    }
    <div className={`live-call-call-controls ${visible ? "show": "hide"}`}>
        <div className={containerStyle}>
            <CallTimer
              conferenceStartDate={conferenceSettings?.start_date}
              conferenceEndDate={conferenceSettings?.end_date}
            />
            {renderMicButton()}
            {cameraAllowed ? renderCamButton() : null}
            {renderDTMFButton()}
            {renderScreenshareButton()}
            {renderSettingsButton()}
            {renderFullscreenButton()}
            {renderParticipantsButton()}
            {chatEnabled ? renderChatButton() : null}
            {dtmfActions.length > 0 ? renderDtmfActionsButton() : null}
            <div className='flex-child flex-2'>
                <Button 
                  color='danger'
                  variant="contained"
                  size="medium"
                //   startIcon={<CallEndIcon/>}
                  onClick={onEnd}
                >
                  {config?.callControls?.endButtonTxt
                      ? config.callControls.endButtonTxt
                      : "End Call"
                  }
                </Button>
            </div>
        </div>
    </div>
    </>
  )
}
